import {defineStore} from 'pinia';
import {ref} from 'vue';
import {AthumiService, ProductPrice} from '@/api/enterprise-gateway-api.proxy.ts';
import {useEnterpriseGatewayApiProxy} from '@/plugins/proxy-client-plugin.ts';
import {useErrorStore} from '@/app/stores/error-store.ts';
import {LoadingState} from '@/app/stores/loading-state.ts';
import type {BasketItem} from "@/app/stores/session-store.ts";

type ServiceInfo = { title: string; info: string, items: BasketItem[] };
type ServiceInfoMap = Record<string, ServiceInfo>;

export const serviceInfoMap: ServiceInfoMap = {
    [AthumiService.ELys]: {
        title: 'eLys',
        info: 'eLys is het digitale platform voor overlijdensadministratie. Het maakt de aangifte en opvolging van een overlijden eenvoudiger, sneller en veiliger voor artsen, uitvaartondernemers, gemeenten en overheden. ',
        items: [
            {
                productCode: 'eLys002',
                productDescription: 'eLys dossier',
                vatPercentage: 21,
                priceExclVat: 25,
                isSelfBilling: false,
            }
        ]
    },
    [AthumiService.Klip]: {
        title: 'KLIP',
        info: 'Met het KLIP kunnen aannemers en andere uitvoerders van werken gedetailleerde informatie opvragen over ondergrondse kabels en leidingen. Zo kunnen ze graafschade en levensbedreigende situaties vermijden.',
        items: [
            {
                productCode: 'KLIP004',
                productDescription: 'KLIP-planaanvraag <200 m²',
                vatPercentage: 21,
                priceExclVat: 6.50,
                isSelfBilling: false,
                metaData: {
                    '(sub)organisatie': 'InfraNova NV',
                    planaanvraagId: crypto.randomUUID(),
                },
            },
            {
                productCode: 'KLIP005',
                productDescription: 'KLIP-planaanvraag >200 m²',
                vatPercentage: 21,
                priceExclVat: 13,
                isSelfBilling: false,
                metaData: {
                    '(sub)organisatie': 'Delta Kabelwerken BV',
                    planaanvraagId: crypto.randomUUID(),
                },
            },
            {
                productCode: 'KLIP006',
                productDescription: 'KLIP IMKL-data ',
                vatPercentage: 21,
                priceExclVat: 6.50,
                isSelfBilling: false,
                metaData: {
                    '(sub)organisatie': 'Geotech Solutions NV',
                    planaanvraagId: crypto.randomUUID(),
                },
            }
        ]
    },
    [AthumiService.Vip]: {
        title: 'VIP',
        info: 'Het Vastgoedinformatieplatform (VIP) maakt gevalideerde vastgoedinformatie snel beschikbaar',
        items: [
            {
                productCode: 'VIP005',
                productDescription: 'Bodemattest - Platformretributie',
                vatPercentage: 21,
                priceExclVat: 5,
                extraInfo: 'Korting aangrenzende percelen',
                isSelfBilling: false,
            },
            {
                productCode: 'VIP002',
                variant: 'BA_DP',
                productDescription: 'Bodemattest Deelperceel- Gewestelijke bronretributie',
                vatPercentage: 0,
                priceExclVat: 269,
                extraInfo: 'Hoeveelheidskorting',
                isSelfBilling: false,
            },
            {
                productCode: 'VIP002',
                variant: 'BA_KP',
                productDescription: 'Bodemattest Kadastraal perceel- Gewestelijke bronretributie',
                vatPercentage: 0,
                priceExclVat: 67,
                extraInfo: 'Hoeveelheidskorting',
                isSelfBilling: false,
            },
            {
                productCode: 'VIP002',
                variant: 'BA_OD',
                productDescription: 'Bodemattest Openbaar domein- Gewestelijke bronretributie',
                vatPercentage: 0,
                priceExclVat: 67,
                extraInfo: 'Hoeveelheidskorting',
                isSelfBilling: false,
            },
            {
                productCode: 'VIP003',
                productDescription: 'Vastgoedinlichtingen voor overdracht - Platformretributie',
                vatPercentage: 21,
                priceExclVat: 38.50,
                extraInfo: 'Vrijstelling lokaal bestuur',
                isSelfBilling: false,
            },
            {
                productCode: 'VIP004',
                variant: '23027',
                productDescription: 'Vastgoedinlichtingen voor overdracht - Bronretributie - Stad Halle',
                vatPercentage: 0,
                priceExclVat: 100,
                extraInfo: 'Hoeveelheidskorting',
                isSelfBilling: true,
            },
            {
                productCode: 'VIP004',
                variant: '23106',
                productDescription: 'Vastgoedinlichtingen voor overdracht - Bronretributie - Gemeente Pajottegem',
                vatPercentage: 0,
                priceExclVat: 150,
                extraInfo: 'Hoeveelheidskorting',
                isSelfBilling: true,
            },
            {
                productCode: 'VIP004',
                variant: '23077',
                productDescription: 'Vastgoedinlichtingen voor overdracht - Bronretributie - Gemeente Sint-Pieters-Leeuw',
                vatPercentage: 0,
                priceExclVat: 60,
                extraInfo: 'Korting aangrenzende percelen',
                isSelfBilling: true,
            },
            {
                productCode: 'VIP007',
                productDescription: 'Maatregelenregister voor overdracht - Platformretributie',
                vatPercentage: 21,
                priceExclVat: 1.40,
                extraInfo: 'Vrijstelling lokaal bestuur',
                isSelfBilling: false,
            },
            {
                productCode: 'VIP008',
                productDescription: 'Maatregelenregister voor overdracht - Bronretributie',
                vatPercentage: 0,
                priceExclVat: 6.50,
                extraInfo: 'Hoeveelheidskorting',
                isSelfBilling: false,
            },
            {
                productCode: 'VIP008',
                productDescription: 'Maatregelenregister voor overdracht - Bronretributie',
                vatPercentage: 0,
                priceExclVat: 6.50,
                extraInfo: 'Hoeveelheidskorting',
                isSelfBilling: true,
            }
        ]
    },
};

export const useProductStore = defineStore('product-store', () => {
    type ProductPriceByKey = Map<string, number>;

    const PRODUCT_VARIANT_KEY_SEPARATOR = ':';

    const apiProxy = useEnterpriseGatewayApiProxy();
    const errorStore = useErrorStore();
    const loadingState = new LoadingState();
    const serviceInfoMapWithProductPrices = ref<ServiceInfoMap>(cloneServiceInfoMap(serviceInfoMap));

    let hasLoaded = false;
    let cachedProducts: ProductPrice[] = [];

    function cloneServiceInfoMap(source: ServiceInfoMap): ServiceInfoMap {
        return Object.fromEntries(
            Object.entries(source).map(([service, serviceInfo]) => [
                service,
                {
                    ...serviceInfo,
                    items: serviceInfo.items.map(item => ({
                        ...item,
                        metaData: item.metaData ? {...item.metaData} : undefined,
                    })),
                },
            ]),
        );
    }

    const createProductPriceKey = (productCode?: string, variant?: string): string | undefined => {
        const normalizedProductCode = productCode?.trim();

        if (!normalizedProductCode) {
            return undefined;
        }

        const normalizedVariant = variant?.trim();

        return normalizedVariant
            ? `${normalizedProductCode}${PRODUCT_VARIANT_KEY_SEPARATOR}${normalizedVariant}`
            : normalizedProductCode;
    };

    const normalizeProductPriceKey = (productCode?: string, variant?: string): string | undefined => {
        return createProductPriceKey(productCode, variant)?.toLowerCase();
    };

    const createProductPriceLookup = (products: readonly ProductPrice[]): ProductPriceByKey => {
        const productPricesByKey: ProductPriceByKey = new Map<string, number>();

        products.forEach(product => {
            const priceKey = normalizeProductPriceKey(product.productCode, product.variant);

            if (!priceKey || product.price === undefined) {
                return;
            }

            productPricesByKey.set(priceKey, product.price);
        });

        return productPricesByKey;
    };

    const getProductPrice = (
        productPricesByKey: ProductPriceByKey,
        productCode?: string,
        variant?: string,
    ): number | undefined => {
        const variantPriceKey = normalizeProductPriceKey(productCode, variant);

        if (!variantPriceKey) {
            return undefined;
        }

        const productPriceKey = normalizeProductPriceKey(productCode);
        const variantPrice = productPricesByKey.get(variantPriceKey);
        const productPrice = productPriceKey ? productPricesByKey.get(productPriceKey) : undefined;

        return variantPrice ?? productPrice;
    };

    const updateServiceItemPrice = (
        item: ServiceInfoMap[string]['items'][number],
        productPricesByKey: ProductPriceByKey,
    ): void => {
        const updatedPrice = getProductPrice(
            productPricesByKey,
            item.productCode,
            item.variant,
        );

        if (updatedPrice === undefined) {
            return;
        }

        item.priceExclVat = updatedPrice;
    };

    const applyProductPrices = (products: readonly ProductPrice[]): void => {
        const updatedServiceInfoMap = cloneServiceInfoMap(serviceInfoMap);
        const productPricesByKey = createProductPriceLookup(products);

        Object.values(updatedServiceInfoMap).forEach(serviceInfo => {
            serviceInfo.items.forEach(item => updateServiceItemPrice(item, productPricesByKey));
        });

        serviceInfoMapWithProductPrices.value = updatedServiceInfoMap;
    };

    const loadProducts = async (service: AthumiService): Promise<ProductPrice[] | undefined> => {
        loadingState.startLoading();

        try {
            const products = await apiProxy.products_GetByService(service);

            cachedProducts = products ?? [];
            applyProductPrices(cachedProducts);
            hasLoaded = true;

            return cachedProducts;
        } catch (error) {
            loadingState.setError();
            await errorStore.setError(error);
        } finally {
            loadingState.stopLoading();
        }
    };

    return {
        isLoading: loadingState.isLoading,
        errorLoading: loadingState.errorLoading,
        serviceInfoMap: serviceInfoMapWithProductPrices,
        loadProducts,
    };
});

